import { useQuery, useMutation } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  GetInvoiceAssociationsResponse,
  DeleteRebateInvoiceResponse,
} from '@erp/rpc';

interface InvoiceFilters {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

/**
 * Fetches invoices for a given rebate agreement.
 * Returns unwrapped data ({ invoices, agreement, pagination }) matching the legacy API shape.
 */
export function useRebateInvoicesByAgreement(
  agreementId: string,
  filters?: InvoiceFilters,
  enabled = true,
) {
  return useQuery({
    ...orpc.rebates.transactionRecords.listByAgreement.queryOptions({
      input: {
        agreementId,
        ...(filters?.startDate && { startDate: filters.startDate }),
        ...(filters?.endDate && { endDate: filters.endDate }),
        ...(filters?.limit && { limit: filters.limit }),
        ...(filters?.offset && { offset: filters.offset }),
      },
    }),
    enabled: enabled && !!agreementId,
    select: (response) => response.data,
  });
}

export function useGetInvoiceAssociations() {
  return useMutation<GetInvoiceAssociationsResponse, Error, string>({
    mutationFn: (invoiceId: string) =>
      rpcClient.rebates.transactionRecords.getAssociations({ invoiceId }),
  });
}

export function useDeleteRebateInvoice() {
  return useMutation<
    DeleteRebateInvoiceResponse,
    Error,
    { invoiceId: string; agreementId: string; force?: boolean }
  >({
    mutationFn: ({ invoiceId, agreementId, force = false }) =>
      rpcClient.rebates.transactionRecords.delete({
        invoiceId,
        agreementId,
        force,
      }),
  });
}
